import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  MenuItem,
  Card,
  Divider,
  Grid,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  LinearProgress,
  Alert,
  Pagination,
  FormControlLabel,
  Switch,
  Chip,
  TableFooter
} from "@mui/material";
import axios from "axios";
import { useLocation } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import GroupIcon from "@mui/icons-material/Group";
import EmailIcon from "@mui/icons-material/Email";
import DescriptionIcon from "@mui/icons-material/Description";
import ReceiptIcon from "@mui/icons-material/Receipt";
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const GeneratePayslip = () => {
  const location = useLocation();

  // Single payslip generation states
  const [payrollId, setPayrollId] = useState(location.state?.employeeId || "");
  const [month, setMonth] = useState(location.state?.month || "");
  const [year, setYear] = useState(location.state?.year || "");
  const [payslip, setPayslip] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [useInitialDeclarations, setUseInitialDeclarations] = useState(true);

  // Bulk generation states
  const [isBulkMode, setIsBulkMode] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(0);
  const [emailProgress, setEmailProgress] = useState(0);
  const [bulkResults, setBulkResults] = useState([]);
  const [emailResults, setEmailResults] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [bulkUseInitialDeclarations, setBulkUseInitialDeclarations] = useState(true);

  const [employeeStatuses, setEmployeeStatuses] = useState({});

  const employeesPerPage = 50;

  useEffect(() => {
    if (location.state?.employeeId) {
      setOpen(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (isBulkMode) {
      fetchEmployees();
    }
  }, [isBulkMode, currentPage]);

  useEffect(() => {
    if (isBulkMode && month && year && employees.length > 0) {
      checkExistingPayslips();
    }
  }, [month, year, employees]);

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear - i);

  const fetchEmployees = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
    setEmployeesLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/payslip/employees/paginated`, {
        params: {
          page: currentPage,
          limit: employeesPerPage
        },
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      const employeeData = response.data.employees || [];
      setEmployees(employeeData);
      setTotalPages(Math.ceil(response.data.totalCount / employeesPerPage));
      setSelectedEmployees([]);
      setSelectAll(false);

      const initialStatuses = {};
      employeeData.forEach(emp => {
        initialStatuses[emp.employee_id] = {
          payslipStatus: 'Not Generated',
          emailStatus: 'Not Sent'
        };
      });
      setEmployeeStatuses(initialStatuses);

    } catch (error) {
      //console.error("Error fetching employees:", error);
      setError("Failed to fetch employees");
    } finally {
      setEmployeesLoading(false);
    }
  };

  const checkExistingPayslips = async () => {
    if (!month || !year || employees.length === 0) return;

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      const employeeIds = employees.map(emp => emp.employee_id);

      const response = await axios.post(`${API_BASE_URL}/api/payslip/check-existing`, {
        employee_ids: employeeIds,
        month,
        year,
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        withCredentials: true
      });

      const existingPayslips = response.data.existing_payslips || [];

      const updatedStatuses = { ...employeeStatuses };

      employeeIds.forEach(empId => {
        if (updatedStatuses[empId]) {
          updatedStatuses[empId].payslipStatus = 'Not Generated';
          updatedStatuses[empId].emailStatus = 'Not Sent';
        }
      });

      existingPayslips.forEach(payslipInfo => {
        if (updatedStatuses[payslipInfo.employee_id]) {
          updatedStatuses[payslipInfo.employee_id].payslipStatus = 'Generated';
          if (payslipInfo.email_sent) {
            const emailStatus = payslipInfo.email_status || 'sent';
            switch (emailStatus.toLowerCase()) {
              case 'sent':
                updatedStatuses[payslipInfo.employee_id].emailStatus = 'Sent';
                break;
              case 'failed':
                updatedStatuses[payslipInfo.employee_id].emailStatus = 'Send Failed';
                break;
              case 'bounced':
                updatedStatuses[payslipInfo.employee_id].emailStatus = 'Bounced';
                break;
              case 'pending':
                updatedStatuses[payslipInfo.employee_id].emailStatus = 'Pending';
                break;
              default:
                updatedStatuses[payslipInfo.employee_id].emailStatus = 'Sent';
            }
          } else {
            if (payslipInfo.email_status === 'pending') {
              updatedStatuses[payslipInfo.employee_id].emailStatus = 'Pending';
            } else if (payslipInfo.email_status === 'failed') {
              updatedStatuses[payslipInfo.employee_id].emailStatus = 'Send Failed';
            } else {
              updatedStatuses[payslipInfo.employee_id].emailStatus = 'Not Sent';
            }
          }
        }
      });

      setEmployeeStatuses(updatedStatuses);

    } catch (error) {
      //console.error("Error checking existing payslips:", error);
    }
  };

  // ========== SINGLE PAYSLIP GENERATION ==========
  const handleSinglePayslipGeneration = async () => {
    setError("");
    setPayslip(null);
    setSuccessMessage("");
    setLoading(true);

    if (!payrollId || !month || !year) {
      setError("Employee ID, Month, and Year are required");
      setLoading(false);
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/payslip/generate?useInitialDeclarations=${useInitialDeclarations}`,
        {
          employee_id: payrollId,
          month,
          year,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          withCredentials: true
        }
      );

      setPayslip(response.data);
      setSuccessMessage(response.data.message || "Payslip generated successfully");
      setOpen(false);
      setLoading(false);
    } catch (error) {
      //console.error("Error generating payslip:", error);
      setError(error.response?.data?.message || "Failed to generate payslip. Please check Employee ID and try again.");
      setLoading(false);
    }
  };

  // ========== BULK PAYSLIP GENERATION ==========
  const handleBulkPayslipGeneration = async () => {
    if (selectedEmployees.length === 0) {
      setError("Please select at least one employee");
      return;
    }

    if (!month || !year) {
      setError("Month and Year are required for bulk generation");
      return;
    }

    setBulkLoading(true);
    setBulkProgress(0);
    setBulkResults([]);
    setError("");
    setSuccessMessage("");

    const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      setBulkLoading(false);
      return;
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/payslip/generate/bulk?useInitialDeclarations=${bulkUseInitialDeclarations}`,
        {
          employee_ids: selectedEmployees,
          month,
          year,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          withCredentials: true
        }
      );

      const results = response.data.results || [];
      setBulkResults(results);
      setBulkProgress(100);

      const updatedStatuses = { ...employeeStatuses };
      results.forEach(result => {
        if (updatedStatuses[result.employee_id]) {
          updatedStatuses[result.employee_id].payslipStatus =
            isResultSuccessful(result) ? 'Generated' : 'Generation Failed';
        }
      });
      setEmployeeStatuses(updatedStatuses);

      const successCount = results.filter(r => isResultSuccessful(r)).length;
      const errorCount = results.filter(r => !isResultSuccessful(r)).length;

      setSuccessMessage(`Payslip generation completed: ${successCount} successful, ${errorCount} failed`);

    } catch (error) {
      //console.error("Error in bulk payslip generation:", error);
      setError(error.response?.data?.message || "Failed to generate bulk payslips");
    } finally {
      setBulkLoading(false);
    }
  };

  // ========== BULK EMAIL SENDING ==========
  const handleBulkEmailSending = async () => {
    const eligibleEmployees = selectedEmployees.filter(empId =>
      employeeStatuses[empId]?.payslipStatus === 'Generated'
    );

    if (eligibleEmployees.length === 0) {
      setError("No employees with generated payslips selected. Please generate payslips first.");
      return;
    }

    if (!month || !year) {
      setError("Month and Year are required for sending emails");
      return;
    }

    setEmailLoading(true);
    setEmailProgress(0);
    setEmailResults([]);
    setError("");

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        setEmailLoading(false);
        return;
      }

      const response = await axios.post(`${API_BASE_URL}/api/payslip/send-emails/bulk`, {
        employee_ids: eligibleEmployees,
        month,
        year,
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        withCredentials: true
      });

      const results = response.data.results || [];
      setEmailResults(results);

      const updatedStatuses = { ...employeeStatuses };
      let processed = 0;
      for (const result of results) {
        if (updatedStatuses[result.employee_id]) {
          if (isResultSuccessful(result)) {
            updatedStatuses[result.employee_id].emailStatus = 'Sent';
          } else {
            const errorMessage = result.message?.toLowerCase() || '';
            if (errorMessage.includes('bounce')) {
              updatedStatuses[result.employee_id].emailStatus = 'Bounced';
            } else if (errorMessage.includes('invalid email') || errorMessage.includes('email not found')) {
              updatedStatuses[result.employee_id].emailStatus = 'Invalid Email';
            } else {
              updatedStatuses[result.employee_id].emailStatus = 'Send Failed';
            }
          }
        }
        processed++;
        setEmailProgress(Math.round((processed / results.length) * 100));
        await new Promise(resolve => setTimeout(resolve, 50));
      }
      setEmployeeStatuses(updatedStatuses);

      const successCount = results.filter(r => isResultSuccessful(r)).length;
      const errorCount = results.filter(r => !isResultSuccessful(r)).length;

      setSuccessMessage(`Email sending completed: ${successCount} sent successfully, ${errorCount} failed`);

      setTimeout(() => {
        checkExistingPayslips();
      }, 1000);

    } catch (error) {
      //console.error("Error in bulk email sending:", error);
      setError(error.response?.data?.message || "Failed to send bulk emails");

      const resetStatuses = { ...employeeStatuses };
      eligibleEmployees.forEach(empId => {
        if (resetStatuses[empId]) {
          resetStatuses[empId].emailStatus = 'Not Sent';
        }
      });
      setEmployeeStatuses(resetStatuses);
    } finally {
      setEmailLoading(false);
    }
  };

  const handleEmployeeSelection = (employeeId) => {
    setSelectedEmployees(prev => {
      if (prev.includes(employeeId)) {
        return prev.filter(id => id !== employeeId);
      } else {
        return [...prev, employeeId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(employees.map(emp => emp.employee_id));
    }
    setSelectAll(!selectAll);
  };

  const isResultSuccessful = (result) => {
    return result.status === 'success' ||
      (result.message && result.message.toLowerCase().includes('successfully')) ||
      (result.message && result.message.toLowerCase().includes('sent'));
  };

  const getStatusChip = (status, type) => {
    let color = 'default';
    let label = status;

    if (type === 'payslip') {
      switch (status) {
        case 'Generated':
          color = 'success';
          break;
        case 'Generation Failed':
          color = 'error';
          break;
        default:
          color = 'default';
      }
    } else if (type === 'email') {
      switch (status) {
        case 'Sent':
          color = 'success';
          break;
        case 'Send Failed':
          color = 'error';
          break;
        case 'Bounced':
          color = 'warning';
          break;
        case 'Invalid Email':
          color = 'error';
          break;
        case 'Pending':
          color = 'info';
          break;
        default:
          color = 'default';
      }
    }

    return (
      <Chip
        label={label}
        color={color}
        size="small"
        variant={color === 'default' ? 'outlined' : 'filled'}
      />
    );
  };

  // ========== FORMAT FUNCTIONS ==========
  const formatCurrency = (value) => {
    return value != null ? value.toLocaleString('en-IN') : '0';
  };

  const shouldDisplayValue = (value) => {
    return value != null && value > 0;
  };

  // ========== RENDER PAYSLIP CARD ==========
const renderPayslipCard = () => {
    if (!payslip) return null;

    const { payslip: payslipData, employeeDetails, financialDetails, tax_details, cumulative_data } = payslip;

    // ============ HELPER FUNCTIONS ============
    const formatCurrency = (value) => {
        return value != null ? Math.round(value).toLocaleString('en-IN') : '0';
    };

    const capitalizeWords = (str) => {
        if (!str) return '';
        return str.replace(/\b\w/g, char => char.toUpperCase());
    };

    const shouldDisplayValue = (value) => {
        return value != null && value > 0;
    };

    // ============ INDIAN NUMBER TO WORDS ============
    const toIndianWords = (num) => {
        if (num === 0) return 'zero';
        if (num < 0) return 'minus ' + toIndianWords(Math.abs(num));
        
        const ones = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine',
            'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
        const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
        
        const convertLessThanThousand = (n) => {
            if (n === 0) return '';
            if (n < 20) return ones[n] + ' ';
            if (n < 100) return tens[Math.floor(n / 10)] + ' ' + (n % 10 !== 0 ? ones[n % 10] + ' ' : '');
            return ones[Math.floor(n / 100)] + ' hundred ' + (n % 100 !== 0 ? convertLessThanThousand(n % 100) : '');
        };
        
        let result = '';
        
        // Crores (1,00,00,000)
        const crores = Math.floor(num / 10000000);
        if (crores > 0) {
            result += convertLessThanThousand(crores) + 'crore ';
            num %= 10000000;
        }
        
        // Lakhs (1,00,000)
        const lakhs = Math.floor(num / 100000);
        if (lakhs > 0) {
            result += convertLessThanThousand(lakhs) + 'lakh ';
            num %= 100000;
        }
        
        // Thousands (1,000)
        const thousands = Math.floor(num / 1000);
        if (thousands > 0) {
            result += convertLessThanThousand(thousands) + 'thousand ';
            num %= 1000;
        }
        
        if (num > 0) {
            result += convertLessThanThousand(num);
        }
        
        return result.trim();
    };

    // ============ CHECK FOR ADDITIONAL PAYMENTS ============
    const hasAdditionalPayments = shouldDisplayValue(payslipData.reimbursements) || 
                                  shouldDisplayValue(payslipData.arrear_included);

    return (
        <Card sx={{ maxWidth: "95%", mx: "auto", my: 4, p: 3, boxShadow: 5 }}>
            {/* ===== HEADER ===== */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5" fontWeight="bold" color="primary">
                    <ReceiptIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                    Payslip
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    Generated on: {new Date().toLocaleDateString('en-IN')}
                </Typography>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* ===== EMPLOYEE & FINANCIAL DETAILS ===== */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                    <Box sx={{ p: 2, bgcolor: "#f5f5f5", borderRadius: 2 }}>
                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                            Employee Details
                        </Typography>
                        <Typography><strong>Employee ID:</strong> {payslipData.employee_id || "N/A"}</Typography>
                        <Typography><strong>Name:</strong> {employeeDetails?.firstName || "N/A"} {employeeDetails?.lastName || "N/A"}</Typography>
                        <Typography><strong>Email:</strong> {employeeDetails?.email || "N/A"}</Typography>
                        <Typography><strong>Phone Number:</strong> {employeeDetails?.phoneNumber || "N/A"}</Typography>
                    </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Box sx={{ p: 2, bgcolor: "#f5f5f5", borderRadius: 2 }}>
                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                            Financial Details
                        </Typography>
                        <Typography><strong>Bank Name:</strong> {financialDetails?.bankName || "N/A"}</Typography>
                        <Typography><strong>IFSC Code:</strong> {financialDetails?.ifscCode || "N/A"}</Typography>
                        <Typography><strong>Account No:</strong> {financialDetails?.accountNumber || "N/A"}</Typography>
                        <Typography><strong>Designation:</strong> {financialDetails?.department || "N/A"}</Typography>
                    </Box>
                </Grid>
            </Grid>

            <Divider sx={{ mb: 3 }} />

            {/* ===== SALARY & DEDUCTIONS ===== */}
            <Grid container spacing={3}>
                {/* ===== LEFT COLUMN: SALARY DETAILS ===== */}
                <Grid item xs={12} md={6}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">
                        Salary Details
                    </Typography>
                    <Box sx={{ border: "1px solid #e0e0e0", borderRadius: 2, p: 2 }}>
                        <Grid container>
                            <Grid item xs={6}>
                                <Typography fontWeight="bold">Component</Typography>
                            </Grid>
                            <Grid item xs={3}>
                                <Typography fontWeight="bold" textAlign="right">Amount</Typography>
                            </Grid>
                            <Grid item xs={3}>
                                <Typography fontWeight="bold" textAlign="right">Cumulative</Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <Divider sx={{ my: 1 }} />
                            </Grid>

                            {/* Base Salary */}
                            <Grid item xs={6}>
                                <Typography>Base Salary</Typography>
                            </Grid>
                            <Grid item xs={3}>
                                <Typography textAlign="right">₹{formatCurrency(payslipData.base_salary)}</Typography>
                            </Grid>
                            <Grid item xs={3}>
                                <Typography textAlign="right">₹{formatCurrency(cumulative_data?.base_salary?.cumulative || 0)}</Typography>
                            </Grid>

                            {/* HRA */}
                            <Grid item xs={6}>
                                <Typography>HRA</Typography>
                            </Grid>
                            <Grid item xs={3}>
                                <Typography textAlign="right">₹{formatCurrency(payslipData.hra)}</Typography>
                            </Grid>
                            <Grid item xs={3}>
                                <Typography textAlign="right">₹{formatCurrency(cumulative_data?.hra?.cumulative || 0)}</Typography>
                            </Grid>

                            {/* Special Allowance */}
                            <Grid item xs={6}>
                                <Typography>Special Allowance</Typography>
                            </Grid>
                            <Grid item xs={3}>
                                <Typography textAlign="right">₹{formatCurrency(payslipData.special_allowance)}</Typography>
                            </Grid>
                            <Grid item xs={3}>
                                <Typography textAlign="right">₹{formatCurrency(cumulative_data?.special_allowance?.cumulative || 0)}</Typography>
                            </Grid>
                             {shouldDisplayValue(payslipData.lop_deduction) && (
                                <>
                                    <Grid item xs={6}>
                                        <Typography sx={{ color: "#d32f2f" }}>Less: LOP ({payslipData.lop_days || 0} days)</Typography>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Typography textAlign="right" sx={{ color: "#d32f2f" }}>-₹{formatCurrency(payslipData.lop_deduction)}</Typography>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Typography textAlign="right" sx={{ color: "#d32f2f" }}>-₹{formatCurrency(cumulative_data?.lop_deduction?.cumulative || 0)}</Typography>
                                    </Grid>
                                </>
                            )}

                            {/* OT Amount */}
                            {shouldDisplayValue(payslipData.ot_amount) && (
                                <>
                                    <Grid item xs={6}>
                                        <Typography sx={{ color: "#1976d2" }}>OT Amount ({payslipData.ot_hours || 0} hrs)</Typography>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Typography textAlign="right" sx={{ color: "#1976d2" }}>₹{formatCurrency(payslipData.ot_amount)}</Typography>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Typography textAlign="right" sx={{ color: "#1976d2" }}>₹{formatCurrency(cumulative_data?.ot_amount?.cumulative || 0)}</Typography>
                                    </Grid>
                                </>
                            )}


                            {/* Variable Salary */}
                            {shouldDisplayValue(payslipData.variable_salary) && (
                                <>
                                    <Grid item xs={6}>
                                        <Typography sx={{ color: "green" }}>Variable Salary</Typography>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Typography textAlign="right" sx={{ color: "green" }}>₹{formatCurrency(payslipData.variable_salary)}</Typography>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Typography textAlign="right" sx={{ color: "green" }}>₹{formatCurrency(cumulative_data?.variable_salary?.cumulative || 0)}</Typography>
                                    </Grid>
                                </>
                            )}

                            {/* Joining Bonus */}
                            {shouldDisplayValue(payslipData.joining_bonus) && (
                                <>
                                    <Grid item xs={6}>
                                        <Typography sx={{ color: "green", fontWeight: "bold" }}>Joining Bonus</Typography>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Typography textAlign="right" sx={{ color: "green", fontWeight: "bold" }}>₹{formatCurrency(payslipData.joining_bonus)}</Typography>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Typography textAlign="right" sx={{ color: "green", fontWeight: "bold" }}>₹{formatCurrency(cumulative_data?.joining_bonus?.cumulative || 0)}</Typography>
                                    </Grid>
                                </>
                            )}

                            <Grid item xs={12}>
                                <Divider sx={{ my: 1 }} />
                            </Grid>

                            {/* Gross Salary */}
                            <Grid item xs={6}>
                                <Typography fontWeight="bold">Gross Salary</Typography>
                            </Grid>
                            <Grid item xs={3}>
                                <Typography fontWeight="bold" textAlign="right" color="primary">₹{formatCurrency(payslipData.gross_salary)}</Typography>
                            </Grid>
                            <Grid item xs={3}>
                                <Typography fontWeight="bold" textAlign="right" color="primary">₹{formatCurrency(cumulative_data?.gross_salary?.cumulative || 0)}</Typography>
                            </Grid>

                            {/* LOP Deduction (if any) */}
                           
                        </Grid>
                    </Box>
                </Grid>

                {/* ===== RIGHT COLUMN: DEDUCTION DETAILS ===== */}
                <Grid item xs={12} md={6}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom color="error">
                        Deduction Details
                    </Typography>
                    <Box sx={{ border: "1px solid #e0e0e0", borderRadius: 2, p: 2 }}>
                        <Grid container>
                            <Grid item xs={6}>
                                <Typography fontWeight="bold">Component</Typography>
                            </Grid>
                            <Grid item xs={3}>
                                <Typography fontWeight="bold" textAlign="right">Amount</Typography>
                            </Grid>
                            <Grid item xs={3}>
                                <Typography fontWeight="bold" textAlign="right">Cumulative</Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <Divider sx={{ my: 1 }} />
                            </Grid>

                            {/* PF */}
                            <Grid item xs={6}>
                                <Typography>PF</Typography>
                            </Grid>
                            <Grid item xs={3}>
                                <Typography textAlign="right">₹{formatCurrency((payslipData.employeepf || 0) + (payslipData.employerpf || 0))}</Typography>
                            </Grid>
                            <Grid item xs={3}>
                                <Typography textAlign="right">₹{formatCurrency((cumulative_data?.employeepf?.cumulative || 0) + (cumulative_data?.employerpf?.cumulative || 0))}</Typography>
                            </Grid>

                            {/* VPF */}
                            {shouldDisplayValue(payslipData.vpf) && (
                                <>
                                    <Grid item xs={6}>
                                        <Typography>VPF</Typography>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Typography textAlign="right">₹{formatCurrency(payslipData.vpf)}</Typography>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Typography textAlign="right">₹{formatCurrency(cumulative_data?.vpf?.cumulative || 0)}</Typography>
                                    </Grid>
                                </>
                            )}

                            {/* Professional Tax */}
                            <Grid item xs={6}>
                                <Typography>Professional Tax</Typography>
                            </Grid>
                            <Grid item xs={3}>
                                <Typography textAlign="right">₹{formatCurrency(payslipData.professional_tax)}</Typography>
                            </Grid>
                            <Grid item xs={3}>
                                <Typography textAlign="right">₹{formatCurrency(cumulative_data?.professional_tax?.cumulative || 0)}</Typography>
                            </Grid>

                            {/* Income Tax */}
                            <Grid item xs={6}>
                                <Typography>Income Tax</Typography>
                            </Grid>
                            <Grid item xs={3}>
                                <Typography textAlign="right">₹{formatCurrency(payslipData.monthly_tax)}</Typography>
                            </Grid>
                            <Grid item xs={3}>
                                <Typography textAlign="right">₹{formatCurrency(cumulative_data?.monthly_tax?.cumulative || 0)}</Typography>
                            </Grid>

                            {/* Gratuity */}
                            <Grid item xs={6}>
                                <Typography>Gratuity</Typography>
                            </Grid>
                            <Grid item xs={3}>
                                <Typography textAlign="right">₹{formatCurrency(payslipData.gratuity)}</Typography>
                            </Grid>
                            <Grid item xs={3}>
                                <Typography textAlign="right">₹{formatCurrency(cumulative_data?.gratuity?.cumulative || 0)}</Typography>
                            </Grid>

                            {/* Medical Insurance */}
                            <Grid item xs={6}>
                                <Typography>Medical Insurance</Typography>
                            </Grid>
                            <Grid item xs={3}>
                                <Typography textAlign="right">₹{formatCurrency(payslipData.medicalInsurance)}</Typography>
                            </Grid>
                            <Grid item xs={3}>
                                <Typography textAlign="right">₹{formatCurrency(cumulative_data?.medicalInsurance?.cumulative || 0)}</Typography>
                            </Grid>

                            {/* Advance Recovery */}
                            {shouldDisplayValue(payslipData.advance_recovery) && (
                                <>
                                    <Grid item xs={6}>
                                        <Typography sx={{ color: "#d32f2f" }}>Advance Recovery</Typography>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Typography textAlign="right" sx={{ color: "#d32f2f" }}>₹{formatCurrency(payslipData.advance_recovery)}</Typography>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Typography textAlign="right" sx={{ color: "#d32f2f" }}>₹{formatCurrency(cumulative_data?.advance_recovery?.cumulative || 0)}</Typography>
                                    </Grid>
                                </>
                            )}

                            <Grid item xs={12}>
                                <Divider sx={{ my: 1 }} />
                            </Grid>

                            {/* Total Deductions */}
                            <Grid item xs={6}>
                                <Typography fontWeight="bold" color="error">Total Deductions</Typography>
                            </Grid>
                            <Grid item xs={3}>
                                <Typography fontWeight="bold" textAlign="right" color="error">₹{formatCurrency(payslipData.total_deductions)}</Typography>
                            </Grid>
                            <Grid item xs={3}>
                                <Typography fontWeight="bold" textAlign="right" color="error">₹{formatCurrency(cumulative_data?.total_deductions?.cumulative || 0)}</Typography>
                            </Grid>
                        </Grid>
                    </Box>
                </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* ===== ADDITIONAL PAYMENTS SECTION ===== */}
            {hasAdditionalPayments && (
                <>
                    <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">
                        Additionals
                    </Typography>
                    <Box sx={{ border: "1px solid #e0e0e0", borderRadius: 2, p: 2, mb: 3 }}>
                        <Grid container>
                            <Grid item xs={6}>
                                <Typography fontWeight="bold">Component</Typography>
                            </Grid>
                            <Grid item xs={3}>
                                <Typography fontWeight="bold" textAlign="right">Amount</Typography>
                            </Grid>
                            <Grid item xs={3}>
                                <Typography fontWeight="bold" textAlign="right">Cumulative</Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <Divider sx={{ my: 1 }} />
                            </Grid>

                            {/* Reimbursements */}
                            {shouldDisplayValue(payslipData.reimbursements) && (
                                <>
                                    <Grid item xs={6}>
                                        <Typography sx={{ color: "#1976d2" }}>Reimbursements</Typography>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Typography textAlign="right" sx={{ color: "#1976d2" }}>₹{formatCurrency(payslipData.reimbursements)}</Typography>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Typography textAlign="right" sx={{ color: "#1976d2" }}>₹{formatCurrency(cumulative_data?.reimbursements?.cumulative || 0)}</Typography>
                                    </Grid>
                                </>
                            )}

                            {/* Arrear */}
                            {shouldDisplayValue(payslipData.arrear_included) && (
                                <>
                                    <Grid item xs={6}>
                                        <Typography sx={{ color: "#ed6c02" }}>Arrear ({payslipData.arrear_source || 'from previous months'})</Typography>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Typography textAlign="right" sx={{ color: "#ed6c02" }}>₹{formatCurrency(payslipData.arrear_included)}</Typography>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Typography textAlign="right" sx={{ color: "#ed6c02" }}>₹{formatCurrency(cumulative_data?.arrear_included?.cumulative || 0)}</Typography>
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                </>
            )}


            {/* ===== NET SALARY ===== */}
            <Box sx={{ p: 3, bgcolor: "#e8f5e9", borderRadius: 2 }}>
                <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} md={4}>
                        <Typography variant="body2" color="textSecondary">Net Salary (Current Month)</Typography>
                        <Typography variant="h4" fontWeight="bold" color="primary">
                            ₹{formatCurrency(payslipData.net_salary)}
                        </Typography>
                    </Grid>

                </Grid>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* ===== AMOUNT IN WORDS ===== */}
            <Typography variant="body1" fontWeight="bold">
                <strong>Rupees in words: </strong>
                {payslipData.net_salary != null 
                    ? `${capitalizeWords(toIndianWords(Math.floor(payslipData.net_salary)))} Only` 
                    : 'Zero Only'}
            </Typography>

            {/* ===== ADDITIONAL INFO ===== */}
            <Box sx={{ mt: 2 }}>
                {shouldDisplayValue(payslipData.medicalInsurance) && (
                    <Typography variant="body2" color="textSecondary">
                        * Medical insurance deduction applied for this month
                    </Typography>
                )}
                {shouldDisplayValue(payslipData.lop_days) && (
                    <Typography variant="body2" color="textSecondary">
                        * LOP of {payslipData.lop_days} days applied
                    </Typography>
                )}
                {shouldDisplayValue(payslipData.ot_hours) && (
                    <Typography variant="body2" color="textSecondary">
                        * OT of {payslipData.ot_hours} hours at ₹{formatCurrency(payslipData.ot_amount / (payslipData.ot_hours || 1))}/hr
                    </Typography>
                )}
                {shouldDisplayValue(payslipData.arrear_included) && (
                    <Typography variant="body2" color="orange">
                        * Arrear amount of ₹{formatCurrency(payslipData.arrear_included)} is included in total payable
                    </Typography>
                )}
            </Box>
        </Card>
    );
};

  // ========== TO WORDS HELPER ==========
  const toWords = (num) => {
    if (num === 0) return 'zero';

    const ones = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];
    const teens = ['ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
    const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
    const thousands = ['', 'thousand', 'million', 'billion'];

    function convertHundreds(num) {
      let result = '';
      if (num > 99) {
        result += ones[Math.floor(num / 100)] + ' hundred ';
        num %= 100;
      }
      if (num > 19) {
        result += tens[Math.floor(num / 10)] + ' ';
        num %= 10;
      }
      if (num > 9) {
        result += teens[num - 10] + ' ';
      } else if (num > 0) {
        result += ones[num] + ' ';
      }
      return result;
    }

    let result = '';
    let thousandCounter = 0;

    while (num > 0) {
      if (num % 1000 !== 0) {
        result = convertHundreds(num % 1000) + thousands[thousandCounter] + ' ' + result;
      }
      num = Math.floor(num / 1000);
      thousandCounter++;
    }

    return result.trim();
  };

  const capitalizeWords = (str) => {
    return str.replace(/\b\w/g, char => char.toUpperCase());
  };

  // ========== MAIN RENDER ==========
  return (
    <>
      {!open && (
        <Box sx={{ p: 3 }}>
          <Box display="flex" gap={2} mb={3}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<PersonIcon />}
              onClick={() => {
                setIsBulkMode(false);
                setOpen(true);
              }}
            >
              Generate Single Payslip
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<GroupIcon />}
              onClick={() => setIsBulkMode(true)}
            >
              Bulk Generate Payslips
            </Button>
          </Box>

          {/* ===== BULK MODE ===== */}
          {isBulkMode && (
            <Card sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom>
                Bulk Payslip Generation & Email Sending
              </Typography>

              <Grid container spacing={2} sx={{ mb: 3 }} alignItems="center">
                <Grid item xs={6} md={3}>
                  <TextField
                    select
                    label="Select Month"
                    variant="outlined"
                    fullWidth
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                  >
                    {months.map((m, index) => (
                      <MenuItem key={index} value={m}>{m}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    select
                    label="Select Year"
                    variant="outlined"
                    fullWidth
                    value={year}
                    onChange={(e) => setYear(e.target.value)}
                  >
                    {years.map((y) => (
                      <MenuItem key={y} value={y}>{y}</MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* ===== BULK DECLARATION TOGGLE ===== */}
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={bulkUseInitialDeclarations}
                        onChange={(e) => setBulkUseInitialDeclarations(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={bulkUseInitialDeclarations ? "📄 Initial Declarations" : "✅ Verified Proofs"}
                  />
                  <Typography variant="caption" color="textSecondary" display="block">
                    {bulkUseInitialDeclarations
                      ? "Tax calculated based on employee's initial declarations"
                      : "Tax calculated based on approved proofs"}
                  </Typography>
                </Grid>
              </Grid>

              {employeesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <LinearProgress sx={{ width: '100%' }} />
                </Box>
              ) : (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
                    <Typography variant="h6">
                      Employees (Page {currentPage} of {totalPages})
                    </Typography>
                    <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectAll}
                            onChange={handleSelectAll}
                            indeterminate={selectedEmployees.length > 0 && selectedEmployees.length < employees.length}
                          />
                        }
                        label="Select All on Page"
                      />
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<DescriptionIcon />}
                        onClick={handleBulkPayslipGeneration}
                        disabled={bulkLoading || selectedEmployees.length === 0}
                      >
                        {bulkLoading ? "Generating..." : `Generate Payslips (${selectedEmployees.length})`}
                      </Button>
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<EmailIcon />}
                        onClick={handleBulkEmailSending}
                        disabled={emailLoading || selectedEmployees.length === 0}
                      >
                        {emailLoading ? "Sending..." : `Send Emails (${selectedEmployees.filter(id => employeeStatuses[id]?.payslipStatus === 'Generated').length})`}
                      </Button>
                    </Box>
                  </Box>

                  {bulkLoading && (
                    <Box sx={{ mb: 2 }}>
                      <LinearProgress variant="determinate" value={bulkProgress} />
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Generating Payslips: {Math.round(bulkProgress)}%
                      </Typography>
                    </Box>
                  )}

                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectAll}
                              onChange={handleSelectAll}
                              indeterminate={selectedEmployees.length > 0 && selectedEmployees.length < employees.length}
                            />
                          </TableCell>
                          <TableCell>Employee ID</TableCell>
                          <TableCell>First Name</TableCell>
                          <TableCell>Last Name</TableCell>
                          <TableCell>Phone Number</TableCell>
                          <TableCell>Payslip Status</TableCell>
                          <TableCell>Email Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {employees.map((employee) => {
                          const isSelected = selectedEmployees.includes(employee.employee_id);
                          const empStatus = employeeStatuses[employee.employee_id] || {
                            payslipStatus: 'Not Generated',
                            emailStatus: 'Not Sent'
                          };

                          return (
                            <TableRow
                              key={employee.employee_id}
                              hover
                              sx={{ backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.08)' : 'inherit' }}
                            >
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={isSelected}
                                  onChange={() => handleEmployeeSelection(employee.employee_id)}
                                />
                              </TableCell>
                              <TableCell>{employee.employee_id}</TableCell>
                              <TableCell>{employee.firstName}</TableCell>
                              <TableCell>{employee.lastName}</TableCell>
                              <TableCell>{employee.phoneNumber}</TableCell>
                              <TableCell>
                                {getStatusChip(empStatus.payslipStatus, 'payslip')}
                              </TableCell>
                              <TableCell>
                                {getStatusChip(empStatus.emailStatus, 'email')}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                      <TableFooter>
                        <TableRow>
                          <TableCell colSpan={2}>
                            <Typography variant="caption" color="textSecondary">
                              Total Employees: {employees.length}
                            </Typography>
                          </TableCell>
                          <TableCell colSpan={2}>
                            <Typography variant="caption" color="textSecondary">
                              Selected: {selectedEmployees.length}
                            </Typography>
                          </TableCell>
                          <TableCell colSpan={3}>
                            <Typography variant="caption" color="textSecondary">
                              {bulkUseInitialDeclarations ? "Using Initial Declarations" : "Using Verified Proofs"}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableFooter>
                    </Table>
                  </TableContainer>

                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={(event, value) => setCurrentPage(value)}
                      color="primary"
                    />
                  </Box>
                </>
              )}

              {(bulkResults.length > 0 || emailResults.length > 0) && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Operation Results
                  </Typography>

                  {bulkResults.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        Payslip Generation Results:
                      </Typography>
                      {bulkResults.map((result, index) => (
                        <Alert
                          key={`payslip-${index}`}
                          severity={isResultSuccessful(result) ? 'success' : 'error'}
                          sx={{ mb: 1 }}
                        >
                          Employee {result.employee_id}: {result.message}
                        </Alert>
                      ))}
                    </Box>
                  )}

                  {emailResults.length > 0 && (
                    <Box>
                      <Typography variant="subtitle1" gutterBottom>
                        Email Sending Results:
                      </Typography>
                      {emailResults.map((result, index) => (
                        <Alert
                          key={`email-${index}`}
                          severity={isResultSuccessful(result) ? 'success' : 'error'}
                          sx={{ mb: 1 }}
                        >
                          Employee {result.employee_id}: {result.message}
                        </Alert>
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            </Card>
          )}
        </Box>
      )}

      {/* ===== SINGLE PAYSLIP DIALOG ===== */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth PaperProps={{
        sx: {
          height: '70vh',
        }
      }}>
        <DialogTitle>
          Generate Single Payslip
          <IconButton onClick={() => setOpen(false)} sx={{ position: "absolute", right: 10, top: 10 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            margin="normal"
            label="Employee ID"
            variant="outlined"
            fullWidth
            value={payrollId}
            onChange={(e) => setPayrollId(e.target.value)}
          />

          <TextField
            select
            label="Select Month"
            variant="outlined"
            fullWidth
            value={month}
            margin="normal"
            onChange={(e) => setMonth(e.target.value)}
          >
            {months.map((m, index) => (
              <MenuItem key={index} value={m}>{m}</MenuItem>
            ))}
          </TextField>

          <TextField
            select
            margin="normal"
            label="Select Year"
            variant="outlined"
            fullWidth
            value={year}
            onChange={(e) => setYear(e.target.value)}
          >
            {years.map((y) => (
              <MenuItem key={y} value={y}>{y}</MenuItem>
            ))}
          </TextField>

          {/* ===== SINGLE DECLARATION TOGGLE ===== */}
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={useInitialDeclarations}
                  onChange={(e) => setUseInitialDeclarations(e.target.checked)}
                  color="primary"
                />
              }
              label={useInitialDeclarations ? "📄 Initial Declarations" : "✅ Verified Proofs"}
            />
            <Typography variant="caption" color="textSecondary" display="block">
              {useInitialDeclarations
                ? "Tax calculated based on employee's initial declarations"
                : "Tax calculated based on approved proofs"}
            </Typography>
          </Box>

          <Box display="flex" justifyContent="center" mt={3} gap={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSinglePayslipGeneration}
              disabled={loading}
            >
              {loading ? "Processing..." : "Get Payslip"}
            </Button>
            <Button variant="outlined" color="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
          </Box>

          {error && (
            <Typography color="error" textAlign="center" mt={2}>{error}</Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== SUCCESS/ERROR MESSAGES ===== */}
      {successMessage && !payslip && (
        <Alert severity="success" sx={{ m: 2 }}>
          {successMessage}
        </Alert>
      )}

      {error && !open && (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      )}

      {/* ===== PAYSLIP CARD ===== */}
      {payslip && renderPayslipCard()}
    </>
  );
};

export default GeneratePayslip;